import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const CTASection = () => {
  const [form, setForm] = useState({ nome: "", empresa: "", telefone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [whatsOpen, setWhatsOpen] = useState(false);
  const [whatsForm, setWhatsForm] = useState({ nome: "", empresa: "", website: "" });

  const handleSubmit = async () => {
    if (!form.nome.trim() || !form.empresa.trim() || !form.telefone.trim() || !form.email.trim()) {
      toast.error("Por favor, preencha nome, site da empresa, telefone e e-mail.");
      return;
    }

    const websiteRegex = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i;
    if (!websiteRegex.test(form.empresa.trim())) {
      toast.error("Informe um site da empresa válido (ex.: https://empresa.com.br).");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Por favor, insira um e-mail válido.");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: form,
      });

      if (error) throw error;

      // GTM dataLayer event
      if (typeof window !== "undefined") {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({ event: "formulario_contato_sucesso" });
      }

      toast.success("Solicitação enviada! Você receberá um e-mail de confirmação em breve.");
      setForm({ nome: "", empresa: "", telefone: "", email: "" });
      setEmailOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar. Tente novamente ou use o WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    setWhatsForm({ nome: form.nome, empresa: form.empresa, website: "" });
    setWhatsOpen(true);
  };

  const sendWhatsApp = () => {
    if (!whatsForm.nome.trim() || !whatsForm.empresa.trim() || !whatsForm.website.trim()) {
      toast.error("Por favor, preencha todos os campos.");
      return;
    }
    const websiteRegex = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i;
    if (!websiteRegex.test(whatsForm.website.trim())) {
      toast.error("Informe um website válido (ex.: https://empresa.com.br).");
      return;
    }
    const msg = encodeURIComponent(
      `Olá, meu nome é ${whatsForm.nome}.\nEmpresa: ${whatsForm.empresa}\nWebsite: ${whatsForm.website}\n\nGostaria de agendar uma conversa confidencial sobre intermediação de conflito societário.`
    );
    window.open(`https://wa.me/5511982227911?text=${msg}`, "_blank");
    setWhatsOpen(false);
  };

  return (
    <section id="contato" className="section-darker py-14 md:py-20">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <h2 className="text-[25px] md:text-[31px] lg:text-[39px] font-heading font-bold leading-[1.2em] tracking-[-1px] text-surface-darker-foreground mb-4">
            Antes que o conflito escale, <span className="text-gradient-amber">converse.</span>
          </h2>
          <p className="text-surface-darker-foreground/70 font-body text-[16px] font-normal mb-2 leading-[1.5em]">
            Agende uma conversa confidencial. Sem compromisso. Sem exposição.
          </p>
          <p className="text-surface-darker-foreground/50 font-body text-[14px] font-normal max-w-lg mx-auto leading-[1.5em]">
            Um espaço seguro para entender se o que você sente é apenas tensão normal — ou um risco societário que precisa ser tratado agora.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button variant="cta" size="xl" onClick={() => setEmailOpen(true)}>
            Contato por e-mail
          </Button>
          <Button variant="cta-outline" size="lg" onClick={handleWhatsApp} className="text-primary border-primary">
            <MessageCircle className="w-5 h-5 mr-2" />
            Contato por WhatsApp
          </Button>
        </div>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-surface-darker border-surface-dark">
          <DialogHeader>
            <DialogTitle className="text-surface-darker-foreground font-heading">
              Contato por e-mail
            </DialogTitle>
            <DialogDescription className="text-surface-darker-foreground/70 font-body">
              Preencha os dados abaixo. Retornaremos em breve com total sigilo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              placeholder="Seu nome *"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              required
              className="bg-surface-dark border-surface-dark text-surface-dark-foreground placeholder:text-surface-dark-foreground/40 h-12 font-body text-[16px]"
            />
            <Input
              placeholder="Site da empresa *"
              value={form.empresa}
              onChange={(e) => setForm({ ...form, empresa: e.target.value })}
              required
              className="bg-surface-dark border-surface-dark text-surface-dark-foreground placeholder:text-surface-dark-foreground/40 h-12 font-body text-[16px]"
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="Telefone *"
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                required
                className="bg-surface-dark border-surface-dark text-surface-dark-foreground placeholder:text-surface-dark-foreground/40 h-12 font-body text-[16px]"
              />
              <Input
                placeholder="E-mail *"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="bg-surface-dark border-surface-dark text-surface-dark-foreground placeholder:text-surface-dark-foreground/40 h-12 font-body text-[16px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="cta" size="lg" onClick={handleSubmit} disabled={loading}>
              {loading ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={whatsOpen} onOpenChange={setWhatsOpen}>
        <DialogContent className="bg-surface-darker border-surface-dark">
          <DialogHeader>
            <DialogTitle className="text-surface-darker-foreground font-heading">
              Antes de continuar
            </DialogTitle>
            <DialogDescription className="text-surface-darker-foreground/70 font-body">
              Preencha os dados abaixo para iniciar a conversa no WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              placeholder="Nome completo *"
              value={whatsForm.nome}
              onChange={(e) => setWhatsForm({ ...whatsForm, nome: e.target.value })}
              className="bg-surface-dark border-surface-dark text-surface-dark-foreground placeholder:text-surface-dark-foreground/40 h-12 font-body text-[16px]"
            />
            <Input
              placeholder="Nome da empresa *"
              value={whatsForm.empresa}
              onChange={(e) => setWhatsForm({ ...whatsForm, empresa: e.target.value })}
              className="bg-surface-dark border-surface-dark text-surface-dark-foreground placeholder:text-surface-dark-foreground/40 h-12 font-body text-[16px]"
            />
            <Input
              placeholder="Website da empresa *"
              value={whatsForm.website}
              onChange={(e) => setWhatsForm({ ...whatsForm, website: e.target.value })}
              className="bg-surface-dark border-surface-dark text-surface-dark-foreground placeholder:text-surface-dark-foreground/40 h-12 font-body text-[16px]"
            />
          </div>
          <DialogFooter>
            <Button variant="cta" size="lg" onClick={sendWhatsApp}>
              <MessageCircle className="w-5 h-5 mr-2" />
              Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CTASection;
